/**
 * Anti-fraud and security utilities for PingEarn
 * Implements device fingerprinting, rate limiting, and duplicate detection
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * Generate a simple device fingerprint based on browser characteristics
 */
export function generateDeviceFingerprint(): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  let fingerprint = '';
  
  // Browser info
  fingerprint += navigator.userAgent;
  fingerprint += navigator.language;
  fingerprint += screen.colorDepth;
  fingerprint += screen.width + 'x' + screen.height;
  fingerprint += new Date().getTimezoneOffset();
  
  // Canvas fingerprinting
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('PingEarn', 2, 2);
    fingerprint += canvas.toDataURL();
  }
  
  // Create hash
  return hashString(fingerprint);
}

/**
 * Simple string hashing function
 */
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Check if user is rate limited for signups
 */
export async function checkSignupRateLimit(): Promise<boolean> {
  const fingerprint = generateDeviceFingerprint();
  const key = `signup_attempts_${fingerprint}`;
  const stored = sessionStorage.getItem(key);
  
  if (!stored) {
    sessionStorage.setItem(key, JSON.stringify({ count: 1, timestamp: Date.now() }));
    return false; // Not rate limited
  }
  
  const data = JSON.parse(stored);
  const hoursSinceFirst = (Date.now() - data.timestamp) / (1000 * 60 * 60);
  
  // Max 5 signup attempts per 24 hours per device
  if (hoursSinceFirst < 24 && data.count >= 5) {
    return true; // Rate limited
  }
  
  // Reset if > 24 hours
  if (hoursSinceFirst >= 24) {
    sessionStorage.setItem(key, JSON.stringify({ count: 1, timestamp: Date.now() }));
  } else {
    data.count += 1;
    sessionStorage.setItem(key, JSON.stringify(data));
  }
  
  return false;
}

/**
 * Record signup attempt
 */
export function recordSignupAttempt(): void {
  const fingerprint = generateDeviceFingerprint();
  localStorage.setItem('device_fingerprint', fingerprint);
}

/**
 * Check for suspicious activity patterns
 */
export async function detectSuspiciousActivity(userId: string): Promise<{
  isSuspicious: boolean;
  reasons: string[];
}> {
  const reasons: string[] = [];
  
  try {
    // Check earning velocity (too many claims too fast)
    const { data: recentEarnings } = await supabase
      .from('earnings')
      .select('created_at, amount')
      .eq('user_id', userId)
      .gte('created_at', new Date(Date.now() - 60 * 60 * 1000).toISOString()) // Last hour
      .order('created_at', { ascending: false });
    
    if (recentEarnings && recentEarnings.length > 50) {
      reasons.push('Unusual earning velocity (>50 claims in 1 hour)');
    }
    
    // Check for impossible geographic patterns (if location data available)
    // This would require location tracking implementation
    
    // Check for duplicate device fingerprints
    const fingerprint = localStorage.getItem('device_fingerprint');
    if (fingerprint) {
      // This would check against a database of known fingerprints
      // For now, we store locally
    }
    
    return {
      isSuspicious: reasons.length > 0,
      reasons
    };
  } catch (error) {
    console.error('Error detecting suspicious activity:', error);
    return { isSuspicious: false, reasons: [] };
  }
}

/**
 * Validate referral code to prevent self-referrals and circular references
 */
export async function validateReferralCode(
  referralCode: string,
  newUserId: string
): Promise<{ valid: boolean; reason?: string }> {
  try {
    // Find referrer by code
    const { data: referrer } = await supabase
      .from('profiles')
      .select('id, referred_by')
      .eq('referral_code', referralCode)
      .single();
    
    if (!referrer) {
      return { valid: false, reason: 'Invalid referral code' };
    }
    
    // Prevent self-referral (same device)
    const deviceFingerprint = generateDeviceFingerprint();
    const referrerFingerprint = localStorage.getItem(`fingerprint_${referrer.id}`);
    
    if (deviceFingerprint === referrerFingerprint) {
      return { valid: false, reason: 'Cannot use your own referral code' };
    }
    
    // Check for circular referrals (A refers B, B refers A)
    if (referrer.referred_by === newUserId) {
      return { valid: false, reason: 'Circular referral detected' };
    }
    
    return { valid: true };
  } catch (error) {
    console.error('Error validating referral:', error);
    return { valid: false, reason: 'Validation error' };
  }
}

/**
 * Monitor for bot-like behavior
 */
export function detectBotBehavior(): boolean {
  // Check for headless browser
  const isHeadless = /headless/i.test(navigator.userAgent);
  
  // Check for automation tools
  const hasAutomation = (window as any).navigator?.webdriver || 
                       (window as any)._phantom || 
                       (window as any).callPhantom;
  
  // Check for unusual timing patterns
  const mouseEvents: number[] = JSON.parse(
    sessionStorage.getItem('mouse_events') || '[]'
  );
  
  const hasMouseActivity = mouseEvents.length > 5;
  
  return isHeadless || hasAutomation || !hasMouseActivity;
}

/**
 * Track mouse activity to differentiate from bots
 */
export function initMouseTracking(): void {
  let events: number[] = JSON.parse(sessionStorage.getItem('mouse_events') || '[]');
  
  const trackMouse = () => {
    events.push(Date.now());
    if (events.length > 10) events = events.slice(-10); // Keep last 10
    sessionStorage.setItem('mouse_events', JSON.stringify(events));
  };
  
  document.addEventListener('mousemove', trackMouse, { once: false });
  document.addEventListener('click', trackMouse, { once: false });
}

/**
 * Get security score for user (0-100, higher is better)
 */
export async function getUserSecurityScore(userId: string): Promise<number> {
  let score = 100;
  
  const suspicious = await detectSuspiciousActivity(userId);
  if (suspicious.isSuspicious) {
    score -= suspicious.reasons.length * 20;
  }
  
  if (detectBotBehavior()) {
    score -= 30;
  }
  
  const deviceFingerprint = localStorage.getItem('device_fingerprint');
  if (!deviceFingerprint) {
    score -= 10; // No fingerprint recorded
  }
  
  return Math.max(0, Math.min(100, score));
}
